import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { debounce, isNil, omitBy } from 'lodash';
import Alert from 'react-s-alert';
import { withNamespaces, WithNamespaces } from 'react-i18next';
import {
  ContributeDialog,
  ResetChangesDialog,
  LocalChangesTable,
  Button,
} from '@wikifactory/voxel-ui';
import { FileMeta, IndexEntry, StatusType } from 'libs/gutClient/types';
import { getCurrentTabIdFromDesktop } from 'libs/gutClient/electronBridge';
import { DiffIcon } from 'icons/common/cadroom';
import { useCADManager } from 'hooks';
import i18next from 'i18next';
import { useResolveConflict, KeepType } from 'hooks/cad/useResolveConflict';
import { MessageType } from 'constants/global';
import { analytics } from 'core/analytics';
import { getFormatObject } from 'libs/rendering/utils';
import {
  carefulPercent,
  concatPaths,
  humanFileSize,
  getLastSegment,
  isIllegalPath,
} from '../utils';
import type { SyncRepoAppManager } from '../appManager';
import { LocalChangesBanner } from './LocalChangesBanner';

interface LocalChangesContainerProps extends WithNamespaces {
  syncRepoAppManager: SyncRepoAppManager;
}

// syncStatus: 'downloading' | 'uploading' | 'conflict' | 'partially' | 'synced' | 'notConnected' | 'uploadFailed' | 'conflictResolved';

// | 'added' | 'modified' | 'deleted' | 'renamed' | 'restored' | 'approved' | 'editing' | 'reviewing' | 'deprecated';
const mapChangeTypeToUIValues = (status: StatusType) => {
  switch (status) {
    case StatusType.Added:
      return 'added';
    case StatusType.Conflicted:
      return 'conflicted';
    case StatusType.Deleted:
      return 'deleted';
    case StatusType.Modified:
      return 'modified';
    case StatusType.NoChange:
      return 'locked';
    default:
      return 'approved';
  }
};

type ChanelData = {
  path: string;
  type: string;
  refreshConflictData: boolean;
};

export const LocalChangesContainer = withNamespaces()(
  observer(({ syncRepoAppManager }: LocalChangesContainerProps) => {
    const {
      uploadsProgress,
      lockingManager,
      connectionStatus,
      remoteHeadRoot,
      contributingManager,
      selectableLocalChangesTableRowsMap,
    } = syncRepoAppManager;

    const { shouldShowDiffOrMeasurement } = useCADManager();

    const [resolveConflictPath, setResolveConflictPath] = useState<string>();

    const handleReceivedConflictMessage = async (event: {
      data: ChanelData;
    }) => {
      const { type, refreshConflictData } = event?.data || {};
      if (type === MessageType.refreshConflictData && refreshConflictData) {
        await syncRepoAppManager.LoadStatus();
      }
    };

    useEffect(() => {
      window.addEventListener('message', handleReceivedConflictMessage);

      return () => {
        window.removeEventListener('message', handleReceivedConflictMessage);
      };
    }, []);

    const dataSource: LocalChangeDataSource[] =
      syncRepoAppManager.selectableLocalChangesTableRows.map(localChange => {
        const fileMetaData: FileMeta | IndexEntry =
          localChange.workingDirMeta || localChange.head;

        const contributionItem =
          contributingManager.contributionItemsMap[localChange.path];

        let syncStatus = 'none';
        let progress = 0;
        if (localChange.type === StatusType.Conflicted) {
          syncStatus = 'conflict';
        } else if (
          localChange.type === StatusType.Added ||
          localChange.type === StatusType.Modified
        ) {
          const uploadProgress = uploadsProgress.get(
            concatPaths(syncRepoAppManager.connectedPath, localChange.path),
          );

          const shouldBeAnUpload =
            contributionItem?.type === StatusType.Added ||
            contributionItem?.type === StatusType.Modified;

          if (uploadProgress) {
            syncStatus = 'uploading';
            progress = carefulPercent(
              uploadProgress.uploadedBytes,
              uploadProgress.totalBytes,
            );
          } else if (shouldBeAnUpload) {
            syncStatus = 'uploadPending';
            if (contributionItem?.hasUploadBeenInitiated) {
              // If the upload started at some point and it's not in progress then it ended
              syncStatus = 'uploadCompleted';
            }
          }
        } else if (localChange.type === StatusType.Deleted) {
          if (contributionItem) {
            syncStatus = 'toBeContributed';
          }
        }

        let changeStatus = mapChangeTypeToUIValues(localChange.type);
        if (changeStatus === 'conflicted') {
          changeStatus = 'conflictModified';
          if (!localChange.conflict.base) changeStatus = 'conflictAdded';
          else if (!localChange.workingDirMeta)
            changeStatus = 'conflictDeleted';
        }

        if (isIllegalPath(localChange.path)) {
          changeStatus = 'illegalName';
        }

        return {
          id: localChange.path,
          // id: localChange.workingDirEntry.hash,
          filename: getLastSegment(localChange.path),
          path: `/${localChange.path.substring(
            0,
            localChange.path.lastIndexOf('/'),
          )}`,
          changeStatus,
          lastLocalChangeTimestamp: fileMetaData?.mtime,
          lastLocalChangeAuthorName: 'author',
          fileSize: humanFileSize(fileMetaData?.size, true),
          syncStatus,
          progress,
          selected: localChange.selected,
          lockedBy: lockingManager.lockedFilesMap[localChange.path],
          fileLocked: !!lockingManager.lockedFilesMap[localChange.path],
          isRemoteLock: !!lockingManager.remoteLockedPathsMap[localChange.path],
          // disabled: !!hasIllegalPath,
        };
      });

    const changesList = ['added', 'modified', 'deleted', 'renamed'].map(
      type => ({
        type,
        files: syncRepoAppManager.selectedContributableLocalChanges
          .filter(change => mapChangeTypeToUIValues(change.type) === type)
          .map(change => ({
            id: change.path,
            name: change.path,
          })),
      }),
    );

    const resetList = dataSource
      .filter(localChange => localChange.selected)
      .filter(localChange => localChange.changeStatus !== 'locked')
      .map(ds => ({
        id: ds.id,
        name: ds.filename,
        revision: 'revision',
        lastLocalChangeTimestamp: ds.lastLocalChangeTimestamp,
        lastLocalChangeAuthorName: ds.lastLocalChangeAuthorName,
        changeStatus: ds.changeStatus,
      }));

    const [isContributeDialogOpen, setContributeDialogOpen] = useState(false);
    const [isResetDialogOpen, setResetDialogOpen] = useState(false);

    const handleDialogConfirm = (data: {
      title?: string;
      description?: string;
    }) => {
      if (!data?.title) {
        Alert.error('Please add a title to your contribution.');
        return;
      }
      setContributeDialogOpen(false);
      contributingManager.Contribute(data.title, data.description);
    };

    const handleDialogClose = () => {
      setContributeDialogOpen(false);
      setResetDialogOpen(false);
    };

    const handleContributeClick = () => {
      if (contributingManager.isContributing) {
        Alert.warning('Another contribution is already in progress.');
      } else {
        if (syncRepoAppManager.selectedContributableLocalChanges.length === 0) {
          syncRepoAppManager.SelectAllLocalChanges(true, true);
        }
        setContributeDialogOpen(true);
      }
    };

    const handleResetChangesClick = () => {
      setResetDialogOpen(true);
    };
    const handleLockClick = () => {
      lockingManager.LockLocalChangesSelection();
    };
    const handleUnlockClick = () => {
      lockingManager.UnlockLocalChangesSelection();
    };

    const handleResetConfirm = () => {
      syncRepoAppManager.resetChanges();
      setResetDialogOpen(false);
    };

    const [isExpanded, setExpanded] = useState(true);

    const whichTypes =
      syncRepoAppManager.repoManager?.parent?.project?.inSpace?.whichTypes;
    const canContribute = whichTypes?.includes('Contribution');

    const toolBarButtonsDisabled =
      !canContribute ||
      syncRepoAppManager.selectedLocalChangesTableRowPaths.length === 0;

    const queryRemoteTreeWhenNoData = async () => {
      if (!remoteHeadRoot?.hash && connectionStatus === 'synced') {
        syncRepoAppManager.QueryRemoteTree();
      }
    };

    // NOTE(david): There shoud be a cleaner way to do this that is not here
    useEffect(() => {
      queryRemoteTreeWhenNoData();
    }, [connectionStatus, remoteHeadRoot?.hash]);

    const alertCatchError = (error: Error) => {
      analytics.logError(error);
      Alert.error(
        error?.message ||
          'Sorry we failed to open your file. Please try again later.',
      );
    };

    const handleViewLocalFile = async (id: string) => {
      await queryRemoteTreeWhenNoData();
      const change = selectableLocalChangesTableRowsMap[id];
      contributingManager.ViewLocalFile(change).catch(alertCatchError);
    };

    const handleShowDiff = async (id: string) => {
      await queryRemoteTreeWhenNoData();
      const change = selectableLocalChangesTableRowsMap[id];
      contributingManager.Diff(change).catch(alertCatchError);
    };

    const handleShowConflict = async (id: string) => {
      await queryRemoteTreeWhenNoData();
      await syncRepoAppManager.LoadStatus();
      const tabId = await getCurrentTabIdFromDesktop();

      const currentFile = dataSource?.find(item => item.id === id);

      const hasConflictWithCurrentFile = currentFile?.syncStatus === 'conflict';
      const { repoId } = syncRepoAppManager;
      const change = selectableLocalChangesTableRowsMap[id];

      contributingManager
        .Diff(
          omitBy(
            {
              ...(change || {}),
              repoId: hasConflictWithCurrentFile ? repoId : null,
              tabId,
            },
            isNil,
          ),
        )
        .catch(alertCatchError);
    };

    const debounceViewLocalFile = debounce(handleViewLocalFile, 500, {
      leading: true,
    });
    const debounceClickShowDiff = debounce(handleShowDiff, 500, {
      leading: true,
    });
    const debounceClickShowConflict = debounce(handleShowConflict, 500, {
      leading: true,
    });

    const handleDriveStatus = (status: string, record: { id: string }) => {
      if (status === 'conflict' && shouldShowDiffOrMeasurement) {
        return (
          <Button
            primaryColor='black-hollow'
            variant='outlined'
            size='small'
            loading={syncRepoAppManager.queryRemoteTeeLoading}
            className=' text-black hover:opacity-70'
            onClick={() => debounceClickShowConflict(record.id)}
          >
            Show Conflict
          </Button>
        );
      }

      return null;
    };

    const handleRenderChangeCell = (
      record: { changeStatus: string; id: string },
      defaultCell: React.ReactNode,
    ) => {
      const formatObject = getFormatObject(record);
      const isGltfFile = formatObject.formatObject.viewer === 'GltfViewer';
      if (
        record.changeStatus === 'modified' &&
        shouldShowDiffOrMeasurement &&
        isGltfFile
      ) {
        return (
          <div className=' flex items-center'>
            {defaultCell}
            <div
              className=' mx-2 w-6 h-6 cursor-pointer hover:opacity-70'
              onClick={() => debounceClickShowDiff(record.id)}
            >
              <DiffIcon />
            </div>
          </div>
        );
      }
      return defaultCell;
    };

    const handleResolveConflictError = () => {
      // analytics.logError(error); // unnecessary in this specific case because
      // caller already did it
      Alert.error(i18next.t('Conflict resolution failed'));
    };

    const resolveConflictConfig: {
      onSuccess?: () => void;
      onError?: () => void;
    } = { onError: handleResolveConflictError }; // to init later after we have the closeConfirmModal reference

    const { confirmModal, openConfirmModal } = useResolveConflict({
      filename: resolveConflictPath
        ? dataSource.find(item => item.id === resolveConflictPath)?.filename
        : null,
      onKeepCloud: async () => {
        await syncRepoAppManager.resolveConflictKeepCloud(
          resolveConflictPath,
          resolveConflictConfig,
        );
      },
      onKeepLocal: async () => {
        await syncRepoAppManager.resolveConflictKeepLocal(
          resolveConflictPath,
          resolveConflictConfig,
        );
      },
    });

    return (
      <>
        <LocalChangesTable
          tableClassName=' mt-6'
          onRenderStatus={handleDriveStatus}
          onRenderChangeCell={handleRenderChangeCell}
          expanded={isExpanded}
          dataSource={dataSource}
          onSelect={syncRepoAppManager.SelectLocalChange}
          onSelectAll={(event: any, selectedAll: boolean) => {
            syncRepoAppManager.SelectAllLocalChanges(selectedAll);
          }}
          banner={
            <LocalChangesBanner syncRepoAppManager={syncRepoAppManager} />
          }
          onAccordionChange={(event: never, expanded: boolean) => {
            setExpanded(expanded);
          }}
          onFilePathClick={(id: string, relativePath: string) => {
            syncRepoAppManager.openPath(id, relativePath);
          }}
          onFileNameClick={(id: string) => {
            debounceViewLocalFile(id);
          }}
          onKeepCloudClick={(id: string) => {
            setResolveConflictPath(id);
            openConfirmModal(KeepType.Cloud);
          }}
          onKeepLocalClick={(id: string) => {
            setResolveConflictPath(id);
            openConfirmModal(KeepType.Local);
          }}
          toolBar={
            {
              // checkedOutTotal: 3,
              onContributeClick: handleContributeClick,
              onResetClick: handleResetChangesClick,
              onLockClick: handleLockClick,
              onUnlockClick: handleUnlockClick,
              buttonsDisabled: toolBarButtonsDisabled,
            }
            // checkedOutTotal?: number;
            // accordionProps?: Partial<AccordionProps>;
            // restButtonProps?: ButtonProps;
            // contributeButtonProps?: ButtonProps;
            // resolveConflictsButtonProps?: ButtonProps;
            // lockButtonProps?: IconButtonProps;
            // unLockButtonProps?: IconButtonProps;
            // onUnlockClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
            // onLockClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
            // onRestClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
            // onContributeClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
            // onResolveConflictsClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
            // onAccordionChange?: (event: React.SyntheticEvent<Element, Event>, expanded: boolean) => void;
          }
        />
        <ContributeDialog
          dialogProps={{
            open: isContributeDialogOpen,
            onClose: handleDialogClose,
          }}
          changesList={changesList}
          onDialogConfirm={handleDialogConfirm}
          onDialogClose={handleDialogClose}
          // titleText?: string;
          // descriptionText?: string;
          // icon?: React.ReactNode;
          // dialogProps: DialogProps;
          // closeButtonProps?: ButtonProps;
          // confirmButtonProps?: ButtonProps;
          // titleStyle?: React.CSSProperties;
          // descriptionStyle?: React.CSSProperties;
          // iconStyle?: React.CSSProperties;
          // onDialogConfirm?: (formData: {
          //     title: string;
          //     description: string;
          //     locked: boolean;
          // }) => void;
          // onDialogClose?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
        />
        <ResetChangesDialog
          dialogProps={{ open: isResetDialogOpen, onClose: handleDialogClose }}
          onDialogClose={handleDialogClose}
          onDialogConfirm={handleResetConfirm}
          defaultExpanded={false}
          resetList={resetList}
        />
        {confirmModal}
      </>
    );
  }),
);
